"use client";

import { useState, useEffect, useCallback } from "react";
import { ChevronRightIcon, LinkIcon, Trash2Icon } from "lucide-react";
import { cmmdApiClient } from "~/environments/cmmdApi";
import type { WellKnownItem } from "~/environments/cmmdApi";
import type { CmmdProjectLink } from "~/hooks/useCmmdProjectLink";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";

// ── Types ──────────────────────────────────────────────────────────────────────

type CmmdItem = WellKnownItem;

type CmmdLinkStep = "workspace" | "campaign" | "project" | "create";
type CmmdLinkMode = "link" | "create";

export function resolveCmmdLinkDialogCopy(mode: CmmdLinkMode): {
  readonly title: string;
  readonly description: string;
} {
  if (mode === "create") {
    return {
      title: "Create CMMD project",
      description:
        "Create a CMMD planning project and connect it to this Forge project in one step.",
    };
  }

  return {
    title: "Link existing CMMD project",
    description:
      "Connect this Forge project to an existing CMMD project so tasks, context, and threads share one project.",
  };
}

export function resolveCmmdLinkEmptyState(
  step: Exclude<CmmdLinkStep, "create">,
  mode: CmmdLinkMode,
): { readonly title: string; readonly description: string } {
  if (step === "workspace") {
    return {
      title: "No workspaces found",
      description: "Forge could not find any CMMD workspaces for this account.",
    };
  }

  if (step === "campaign") {
    return {
      title: "No campaigns found",
      description:
        mode === "create"
          ? "A CMMD project needs a campaign before Forge can create and link it."
          : "This workspace has no campaigns available to browse.",
    };
  }

  return {
    title: "No CMMD projects yet",
    description: "Create a project in this campaign, then link it to this Forge project.",
  };
}

// ── Fetch helpers ──────────────────────────────────────────────────────────────

async function fetchWorkspaces(): Promise<CmmdItem[]> {
  return Array.from(await cmmdApiClient.listCmmdWorkspaces());
}

async function fetchCampaigns(workspaceId: number): Promise<CmmdItem[]> {
  return Array.from(await cmmdApiClient.listCmmdCampaigns({ workspaceId }));
}

async function fetchProjects(campaignId: number): Promise<CmmdItem[]> {
  return Array.from(await cmmdApiClient.listCmmdProjects({ campaignId }));
}

async function createProject(
  name: string,
  campaignId: number,
  organizationId: number,
): Promise<CmmdItem> {
  return cmmdApiClient.createCmmdProject({ name, campaignId, organizationId });
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function ItemList({
  items,
  loading,
  error,
  emptyState,
  onSelect,
}: {
  items: CmmdItem[];
  loading: boolean;
  error: string | null;
  emptyState: { readonly title: string; readonly description: string };
  onSelect: (item: CmmdItem) => void;
}) {
  if (loading) {
    return (
      <div className="flex flex-col gap-1.5 py-1">
        {["s1", "s2", "s3", "s4"].map((k) => (
          <Skeleton key={k} className="h-9 rounded-md" />
        ))}
      </div>
    );
  }
  if (error) {
    return <p className="py-4 text-sm text-destructive">{error}</p>;
  }
  if (items.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border/70 px-3 py-5">
        <div className="text-sm font-medium text-foreground">{emptyState.title}</div>
        <p className="mt-1 text-sm leading-5 text-muted-foreground">{emptyState.description}</p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-border/40">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            onClick={() => onSelect(item)}
            className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-accent transition-colors duration-100"
          >
            <span className="truncate">{item.name}</span>
            <ChevronRightIcon className="size-3.5 shrink-0 text-muted-foreground/50" />
          </button>
        </li>
      ))}
    </ul>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export interface LinkToCmmdSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cwd: string;
  existingLink: CmmdProjectLink | null;
  initialMode?: CmmdLinkMode;
  defaultProjectName?: string;
  onLink: (
    link: Omit<
      CmmdProjectLink,
      "forgeEnvironmentId" | "forgeProjectId" | "forgeWorkspaceRoot" | "linkedByUserId"
    >,
  ) => Promise<void>;
  onUnlink: () => Promise<void>;
}

export function LinkToCmmdSheet({
  open,
  onOpenChange,
  existingLink,
  initialMode = "link",
  defaultProjectName = "",
  onLink,
  onUnlink,
}: LinkToCmmdSheetProps) {
  const [step, setStep] = useState<CmmdLinkStep>("workspace");

  const [workspaces, setWorkspaces] = useState<CmmdItem[]>([]);
  const [campaigns, setCampaigns] = useState<CmmdItem[]>([]);
  const [projects, setProjects] = useState<CmmdItem[]>([]);

  const [selectedWorkspace, setSelectedWorkspace] = useState<CmmdItem | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<CmmdItem | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset state when the modal opens.
  useEffect(() => {
    if (!open) return;
    setStep("workspace");
    setSelectedWorkspace(null);
    setSelectedCampaign(null);
    setWorkspaces([]);
    setCampaigns([]);
    setProjects([]);
    setError(null);
    setNewProjectName(defaultProjectName);
    setLoading(true);
    fetchWorkspaces()
      .then(setWorkspaces)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load workspaces"))
      .finally(() => setLoading(false));
  }, [defaultProjectName, open]);

  const handleSelectWorkspace = useCallback((ws: CmmdItem) => {
    setSelectedWorkspace(ws);
    setStep("campaign");
    setLoading(true);
    setError(null);
    fetchCampaigns(ws.id)
      .then(setCampaigns)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load campaigns"))
      .finally(() => setLoading(false));
  }, []);

  const handleSelectCampaign = useCallback(
    (campaign: CmmdItem) => {
      setSelectedCampaign(campaign);
      setError(null);
      if (initialMode === "create") {
        setStep("create");
        return;
      }
      setStep("project");
      setLoading(true);
      fetchProjects(campaign.id)
        .then(setProjects)
        .catch((e) => setError(e instanceof Error ? e.message : "Failed to load projects"))
        .finally(() => setLoading(false));
    },
    [initialMode],
  );

  const handleSelectProject = useCallback(
    async (project: CmmdItem) => {
      if (!selectedWorkspace || !selectedCampaign) return;
      setSaving(true);
      try {
        await onLink({
          cmmdOrganizationId: selectedWorkspace.id,
          cmmdProjectId: project.id,
          cmmdCampaignId: selectedCampaign.id,
          cmmdWorkspaceId: selectedWorkspace.id,
          cmmdWorkspaceName: selectedWorkspace.name,
          workspaceName: selectedWorkspace.name,
          cmmdCampaignName: selectedCampaign.name,
          campaignName: selectedCampaign.name,
          cmmdProjectName: project.name,
          projectName: project.name,
          githubRepoFullName: null,
          source: "linked_existing",
          linkedAt: new Date().toISOString(),
          lastVerifiedAt: null,
        });
        onOpenChange(false);
      } finally {
        setSaving(false);
      }
    },
    [selectedWorkspace, selectedCampaign, onLink, onOpenChange],
  );

  const handleCreateProject = useCallback(async () => {
    if (!selectedWorkspace || !selectedCampaign || !newProjectName.trim()) return;
    setSaving(true);
    try {
      const created = await createProject(
        newProjectName.trim(),
        selectedCampaign.id,
        selectedWorkspace.id,
      );
      await onLink({
        cmmdOrganizationId: selectedWorkspace.id,
        cmmdProjectId: created.id,
        cmmdCampaignId: selectedCampaign.id,
        cmmdWorkspaceId: selectedWorkspace.id,
        cmmdWorkspaceName: selectedWorkspace.name,
        workspaceName: selectedWorkspace.name,
        cmmdCampaignName: selectedCampaign.name,
        campaignName: selectedCampaign.name,
        cmmdProjectName: created.name,
        projectName: created.name,
        githubRepoFullName: null,
        source: "created_from_forge",
        linkedAt: new Date().toISOString(),
        lastVerifiedAt: null,
      });
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create project");
    } finally {
      setSaving(false);
    }
  }, [selectedWorkspace, selectedCampaign, newProjectName, onLink, onOpenChange]);

  const handleUnlink = useCallback(async () => {
    setSaving(true);
    try {
      await onUnlink();
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }, [onUnlink, onOpenChange]);

  const stepTitle =
    step === "workspace"
      ? "Select workspace"
      : step === "campaign"
        ? `${selectedWorkspace?.name} — Select campaign`
        : step === "create"
          ? "Create project"
          : `${selectedCampaign?.name} — Select project`;

  const dialogCopy = resolveCmmdLinkDialogCopy(initialMode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-2xl" showCloseButton>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="size-4" />
            {dialogCopy.title}
          </DialogTitle>
          <DialogDescription>{dialogCopy.description}</DialogDescription>
          <p className="text-xs text-muted-foreground">{stepTitle}</p>
          {existingLink && (
            <div className="mt-1 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              Currently linked:{" "}
              <span className="font-medium text-foreground">
                {existingLink.workspaceName} / {existingLink.campaignName} /{" "}
                {existingLink.projectName}
              </span>
            </div>
          )}
        </DialogHeader>

        <DialogPanel>
          {step === "workspace" && (
            <ItemList
              items={workspaces}
              loading={loading}
              error={error}
              emptyState={resolveCmmdLinkEmptyState("workspace", initialMode)}
              onSelect={handleSelectWorkspace}
            />
          )}

          {step === "campaign" && (
            <>
              <button
                type="button"
                onClick={() => setStep("workspace")}
                className="mb-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to workspaces
              </button>
              <ItemList
                items={campaigns}
                loading={loading}
                error={error}
                emptyState={resolveCmmdLinkEmptyState("campaign", initialMode)}
                onSelect={handleSelectCampaign}
              />
            </>
          )}

          {step === "project" && (
            <>
              <button
                type="button"
                onClick={() => setStep("campaign")}
                className="mb-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to campaigns
              </button>
              <ItemList
                items={projects}
                loading={loading}
                error={error}
                emptyState={resolveCmmdLinkEmptyState("project", initialMode)}
                onSelect={(p) => void handleSelectProject(p)}
              />
              {!loading && !error && (
                <button
                  type="button"
                  onClick={() => setStep("create")}
                  className="mt-2 w-full rounded-md border border-dashed border-border py-2 text-xs text-muted-foreground hover:border-ring hover:text-foreground transition-colors duration-150"
                >
                  + Create new project
                </button>
              )}
            </>
          )}

          {step === "create" && (
            <>
              <button
                type="button"
                onClick={() => setStep("project")}
                className="mb-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to projects
              </button>
              <div className="flex flex-col gap-3">
                <label className="text-xs font-medium text-foreground">Project name</label>
                <input
                  className="rounded-md border border-border bg-secondary px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-ring focus:outline-none"
                  placeholder="My Project"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleCreateProject();
                  }}
                  autoFocus
                />
                {error && <p className="text-xs text-destructive">{error}</p>}
                <button
                  type="button"
                  disabled={!newProjectName.trim() || saving}
                  onClick={() => void handleCreateProject()}
                  className="rounded-md bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-150"
                >
                  {saving ? "Creating…" : "Create & link"}
                </button>
              </div>
            </>
          )}
        </DialogPanel>

        {existingLink && (
          <DialogFooter variant="bare">
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleUnlink()}
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-destructive hover:bg-destructive-surface transition-colors duration-150 disabled:opacity-50"
            >
              <Trash2Icon className="size-3" />
              Unlink
            </button>
          </DialogFooter>
        )}
      </DialogPopup>
    </Dialog>
  );
}
